/* eslint-disable @typescript-eslint/no-unused-vars */
'use client'

import { Box, Stack } from '@mui/material';
import dynamic from 'next/dynamic';
import { useRouter, useSearchParams } from 'next/navigation';
import Grid from '@mui/material/Grid2';
import { useAdmissionChance } from "@/context/admission-chance";
import * as yup from "yup";
import { useFormik } from "formik";
import { PUT } from "@/api/put-request";
import { GET } from "@/api/get-request";
import { POST } from "@/api/post-requests";
import React from 'react';
import { useNotifications } from '@toolpad/core/useNotifications';
import { useTranslations } from 'next-intl';
const MultipleSelectChip = dynamic(() => import('@/components/common/multiple-select'), { ssr: false });
const Section = dynamic(() => import('@/components/pages/admission-chance/main-section'), { ssr: false });
const Input = dynamic(() => import('@/components/common/input'), { ssr: false });
const ButtonStepper = dynamic(() => import('@/components/common/button-stepper'), { ssr: false });
const Select = dynamic(() => import('@/components/common/select'), { ssr: false });


export default function Page() {
    const router = useRouter();
    const [loading, setLoading] = React.useState(false);
    const [countries, setCountries] = React.useState<{ code: string, name: string }[]>([]);
    const [languages, setLanguages] = React.useState<{ value: string, label: string }[]>([]);
    const [provinces, setProvinces] = React.useState<{ value: string, label: string }[]>([]);
    const [uuid, setUuid] = React.useState<string | null>(null);
    const { apiData, setApiData } = useAdmissionChance();
    const notifications = useNotifications();
    const searchParams = useSearchParams();
    const t = useTranslations('dana.admission_chance.basic_profile');

    if (!uuid && searchParams && searchParams.get('UUID')) {
        setUuid(searchParams.get('UUID'));
    }

    React.useLayoutEffect(() => {
        const GetSelectData = async (url: string) => {
            const response = await GET({ url: `v1/enums/${url}` });
            if (response.success)
                return response.data
            else return []
        };

        const GetData = async () => {
            setCountries(await GetSelectData('countries'));
            setProvinces(await GetSelectData('target_provinces'));

            if (!uuid) {
                const initiate = await POST({ url: 'v1/admission-chances/initiate', body: {} });
                if (initiate.success) {
                    setUuid(initiate.data.uuid);

                } else {
                    notifications.show(initiate.message, {
                        severity: 'error',
                        autoHideDuration: 3000,
                    });
                    setLoading(false);
                    return;
                }
            }
        };

        GetData();

        setLanguages([
            { value: 'en', label: t('languages.en') },
            { value: 'fr', label: t('languages.fr') },
            { value: 'ar', label: t('languages.ar') },
        ])

    }, []);

    const validationSchema = yup.object({
        country_code: yup.string().required(t('inputs.country_code.error')),
        preferred_language: yup.string().required(t('inputs.preferred_language.error')),
        age: yup.number().typeError(t('inputs.age.error')).required(t('inputs.age.error')).min(0, t('inputs.age.error')).max(120, t('inputs.age.error')),
        interested_provinces: yup.array().min(1, t('inputs.interested_provinces.error')),
    });

    const formik = useFormik({
        initialValues: {
            country_code: apiData?.admission_chance?.country?.code || apiData?.admission_chance?.country_code || "",
            preferred_language: apiData?.admission_chance?.preferred_language || "",
            age: apiData?.admission_chance?.age || "",
            interested_provinces: apiData?.admission_chance?.interested_provinces || [],
        },
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setLoading(true);

            const response = await PUT({
                url: `v1/admission-chances/${uuid}`,
                body: {
                    step: 1,
                    country_code: values.country_code,
                    preferred_language: values.preferred_language,
                    age: Number(values.age),
                    interested_provinces: values.interested_provinces,
                }
            });

            if (response.success) {
                setApiData((prev: any) => ({ ...prev, admission_chance: response.data }));
                router.push('/admission-chance/academic?UUID=' + uuid);
            } else {
                // Set field-specific errors
                if (response.errors) {
                    Object.keys(response.errors).forEach((key) => {
                        const error = response.errors[key];
                        const errorMessage = Array.isArray(error) ? error[0] : error;
                        formik.setFieldError(key, errorMessage);
                        formik.setFieldTouched(key, true, false);
                    });
                }

                notifications.show(response.message, {
                    severity: 'error',
                    autoHideDuration: 3000,
                });
            }
            setLoading(false);
        }
    });

    return <Box
        width={'100%'}
        height={'100%'}
    >
        <Grid
            container
            direction={'column'}
            height={'100%'}
        >
            <Grid
                flex={1}
                container
            >
                <Section>
                    <Stack
                        direction={'column'}
                        spacing={4}
                        useFlexGap
                    >

                        <Select
                            label={t('inputs.country_code.label')}
                            placeholder={t('inputs.country_code.placeholder')}
                            id='country_code'
                            key='country_code'
                            val={formik.values.country_code}
                            list={countries.map((country: { code: string, name: string }) => ({ name: country.name, value: country.code }))}
                            name='country_code'
                            handleChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.country_code && Boolean(formik.errors.country_code)}
                            helper_text={formik.touched.country_code && formik.errors.country_code}
                            disabled={loading}
                        />


                        <Select
                            label={t('inputs.preferred_language.label')}
                            placeholder={t('inputs.preferred_language.placeholder')}
                            id='preferred_language'
                            key='preferred_language'
                            val={formik.values.preferred_language}
                            list={languages.map((language: { value: string, label: string }) => ({ name: language.label, value: language.value }))}
                            name='preferred_language'
                            handleChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.preferred_language && Boolean(formik.errors.preferred_language)}
                            helper_text={formik.touched.preferred_language && formik.errors.preferred_language}
                            disabled={loading}
                        />

                        <Input
                            label={t('inputs.age.label')}
                            placeholder={t('inputs.age.placeholder')}
                            id='age'
                            key='age'
                            name='age'
                            value={formik.values.age.toString()}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.age && Boolean(formik.errors.age)}
                            disabled={loading}
                        />

                        <MultipleSelectChip
                            list={provinces.map((cnt: { value: string, label: string }) => ({ name: cnt.label, value: cnt.value }))}
                            id='interested_provinces'
                            key={'interested_provinces'}
                            val={formik.values.interested_provinces}
                            label={t('inputs.interested_provinces.label')}
                            name={'interested_provinces'}
                            height='40px'
                            handleChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.interested_provinces && Boolean(formik.errors.interested_provinces)}
                            helper_text={formik.touched.interested_provinces && formik.errors.interested_provinces}
                        />

                    </Stack>
                </Section>
            </Grid>
            <Grid>
                <ButtonStepper
                    submit_button_text={t('submit_btn')}
                    skip_button={false}
                    back_button={false}
                    className="mt-8"
                    handleSubmit={formik.handleSubmit}
                    handleBack={() => { }}
                />
            </Grid>
        </Grid>
    </Box>
};